import React from 'react';

import { CONTRIBUTION_MODES } from '@lumapps/contents/constants';
import { getCurrentContributionMode } from '@lumapps/contents/ducks/selectors';
import { Divider } from '@lumapps/lumx/react';
import { PlaylistFieldOrder } from '@lumapps/play-playlists/types';
import { VideoItemFieldOrder } from '@lumapps/play-video/types';
import { useSelector } from '@lumapps/redux/react';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { DEFAULT_WIDGET_PROPERTIES } from '../../constants';
import { WidgetPlaylistProperties } from '../../types';
import { switchPropertiesOptions } from '../../utils';
import { PlaylistMetadataSelection } from './PlaylistMetadataSelection';
import { PlaylistUploader } from './PlaylistUploader';
import { VideoMetadataSelection } from './VideoMetadataSelection';

import './index.scss';

export interface WidgetPlaySettingsProps {
    properties: WidgetPlaylistProperties;
    onChange: (properties: WidgetPlaylistProperties) => void;
}

const CLASSNAME = 'widget-playlist-settings';

export const WidgetPlaylistSettings = ({ properties, onChange }: WidgetPlaySettingsProps) => {
    const widgetProperties: WidgetPlaylistProperties = { ...DEFAULT_WIDGET_PROPERTIES, ...properties };

    const currentContributionMode = useSelector(getCurrentContributionMode);
    const inputLanguage = useSelector(inputLanguageSelector);

    const handlePlaylistFieldOrderChange =
        <K extends keyof WidgetPlaylistProperties>(fieldName: K) =>
        (nextValue: PlaylistFieldOrder) => {
            switchPropertiesOptions<PlaylistFieldOrder>({
                properties: widgetProperties,
                prop: widgetProperties.playlistFieldOrder,
                fieldName,
                callback: onChange,
                nextValue,
            });
        };

    const handleVideoItemFieldChange =
        <K extends keyof WidgetPlaylistProperties>(fieldName: K) =>
        (nextValue: VideoItemFieldOrder) => {
            if (fieldName === 'videoItemFieldOrder') {
                switchPropertiesOptions<VideoItemFieldOrder>({
                    properties: widgetProperties,
                    prop: widgetProperties.videoItemFieldOrder,
                    fieldName,
                    callback: onChange,
                    nextValue,
                });
            } else {
                onChange({
                    ...widgetProperties,
                    [fieldName]: nextValue,
                });
            }
        };

    const onRemovePlaylist = () => {
        const currentPlaylist = typeof widgetProperties?.playlist === 'object' ? { ...widgetProperties.playlist } : {};

        delete currentPlaylist[inputLanguage];

        const hasPlaylists = Object.keys(currentPlaylist).some(
            (lang) => currentPlaylist[lang]?.ids && currentPlaylist[lang].ids.length > 0,
        );

        onChange({
            ...widgetProperties,
            playlist: hasPlaylists ? currentPlaylist : undefined,
        });
    };

    const {
        playlist: playlistProperties,
        playlistFieldOrder,
        videoItemFieldOrder,
        maxNumber,
        viewPositionVariant,
    } = widgetProperties;

    return (
        <div className={CLASSNAME}>
            <PlaylistUploader
                properties={{ playlist: playlistProperties }}
                onChange={(newPlaylist) => onChange({ ...widgetProperties, ...newPlaylist })}
                onPlaylistRemove={onRemovePlaylist}
            />
            {currentContributionMode !== CONTRIBUTION_MODES.WRITER ? (
                <>
                    {viewPositionVariant === 'grid' && (
                        <>
                            <Divider />
                            <PlaylistMetadataSelection
                                properties={{ playlistFieldOrder }}
                                onFieldChange={handlePlaylistFieldOrderChange}
                            />
                        </>
                    )}
                    <Divider />
                    <VideoMetadataSelection
                        properties={{ videoItemFieldOrder, maxNumber, viewPositionVariant }}
                        onFieldChange={handleVideoItemFieldChange}
                    />
                </>
            ) : null}
        </div>
    );
};
